/**
 * Service for fetching Encompass eFolder documents synced from webhooks
 * Data comes from encompass_documents and encompass_attachments tables
 */

import { supabase } from '@/utils/supabase';
import type {
  EncompassAttachment,
  EncompassAttachmentMatchInfo,
  EncompassDocumentWithAttachments,
  LoanDocumentsSummary,
  EncompassDocumentGroup
} from '@/types/encompass-documents';

// Explicit column list — matches EncompassAttachment fields exactly. Used in
// every list/detail SELECT so we don't accidentally pull extracted_text (up to
// 7 MB per row), classification_result, or extraction_metadata into the client.
const ATTACHMENT_LIST_COLUMNS =
  'id,encompass_loan_id,encompass_document_id,attachment_id,title,file_name,' +
  'file_type,file_size,uploaded_date,uploaded_by,is_active,assigned_folder,' +
  'storage_path,storage_bucket,download_status,download_error,synced_at,created_at';

/**
 * Fetches all documents and attachments for a loan, grouped by folder
 */
export async function getEncompassDocuments(
  loanId: string
): Promise<EncompassDocumentWithAttachments[]> {
  // Fetch documents (folders)
  const { data: documents, error: docError } = await supabase
    .from('encompass_documents')
    .select('*')
    .eq('encompass_loan_id', loanId)
    .order('title', { ascending: true });

  if (docError) {
    console.error('Failed to fetch encompass documents:', docError);
    throw new Error(`Failed to fetch documents: ${docError.message}`);
  }

  // Fetch all attachments for this loan. Explicit columns (not select('*'))
  // so we never pull extracted_text / classification_result / extraction_metadata
  // into the browser — those can each be megabytes per row.
  const { data: attachments, error: attError } = await supabase
    .from('encompass_attachments')
    .select(ATTACHMENT_LIST_COLUMNS)
    .eq('encompass_loan_id', loanId)
    .order('title', { ascending: true });

  if (attError) {
    console.error('Failed to fetch encompass attachments:', attError);
    throw new Error(`Failed to fetch attachments: ${attError.message}`);
  }

  // Group attachments by document ID
  const attachmentsByDoc = new Map<string, EncompassAttachment[]>();
  const orphanAttachments: EncompassAttachment[] = [];

  for (const att of (attachments || [])) {
    if (att.encompass_document_id) {
      const existing = attachmentsByDoc.get(att.encompass_document_id) || [];
      existing.push(att);
      attachmentsByDoc.set(att.encompass_document_id, existing);
    } else {
      // Attachments without a document ID go to orphans
      orphanAttachments.push(att);
    }
  }

  // Combine documents with their attachments
  const result: EncompassDocumentWithAttachments[] = (documents || []).map(doc => ({
    ...doc,
    attachments: attachmentsByDoc.get(doc.id) || []
  }));

  // If there are orphan attachments, create a virtual "Unassigned" folder
  if (orphanAttachments.length > 0) {
    result.push({
      id: 'unassigned',
      encompass_loan_id: loanId,
      document_id: 'unassigned',
      title: 'Unassigned Documents',
      status: null,
      category: null,
      requested_date: null,
      received_date: null,
      reviewed_date: null,
      attachment_count: orphanAttachments.length,
      synced_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      attachments: orphanAttachments
    });
  }

  return result;
}

/**
 * Gets a summary of document sync status for a loan
 * Only counts active (current version) attachments, not old/superseded versions
 */
export async function getLoanDocumentsSummary(
  loanId: string
): Promise<LoanDocumentsSummary> {
  const { data: attachments, error } = await supabase
    .from('encompass_attachments')
    .select('download_status, synced_at, is_active')
    .eq('encompass_loan_id', loanId)
    .neq('is_active', false); // Exclude inactive (old version) attachments

  if (error) {
    throw new Error(`Failed to fetch summary: ${error.message}`);
  }

  const { count: docsCount } = await supabase
    .from('encompass_documents')
    .select('*', { count: 'exact', head: true })
    .eq('encompass_loan_id', loanId);

  const downloadedCount = attachments?.filter(a => a.download_status === 'downloaded').length || 0;
  const pendingCount = attachments?.filter(a => a.download_status === 'pending').length || 0;
  const failedCount = attachments?.filter(a => a.download_status === 'failed').length || 0;

  // Find the most recent sync time
  const lastSyncAt = attachments?.reduce((latest, att) => {
    if (!att.synced_at) {
return latest;
}
    if (!latest) {
return att.synced_at;
}
    return new Date(att.synced_at) > new Date(latest) ? att.synced_at : latest;
  }, null as string | null);

  return {
    loanId,
    documentsCount: docsCount || 0,
    attachmentsCount: attachments?.length || 0,
    downloadedCount,
    pendingCount,
    failedCount,
    lastSyncAt
  };
}

/**
 * Gets a signed URL for viewing/downloading an attachment from Supabase Storage
 */
export async function getAttachmentUrl(
  storagePath: string,
  expiresIn = 300
): Promise<string | null> {
  if (!storagePath) {
return null;
}

  const { data, error } = await supabase.storage
    .from('loan-documents')
    .createSignedUrl(storagePath, expiresIn);

  if (error) {
    console.error('Failed to get signed URL:', error);
    return null;
  }

  return data.signedUrl;
}

/**
 * Gets a single attachment by ID
 */
export async function getAttachmentById(
  attachmentId: string
): Promise<EncompassAttachment | null> {
  // Explicit columns — see ATTACHMENT_LIST_COLUMNS note about avoiding
  // extracted_text / classification_result leaks into the browser.
  const { data, error } = await supabase
    .from('encompass_attachments')
    .select(ATTACHMENT_LIST_COLUMNS)
    .eq('id', attachmentId)
    .single();

  if (error) {
    console.error('Failed to fetch attachment:', error);
    return null;
  }

  return data;
}

/**
 * Loan-scoped content-search match returned by the
 * search_loan_attachment_content RPC. snippet uses [[MARK]] / [[/MARK]]
 * sentinels around highlighted terms — render through HighlightedSnippet,
 * not dangerouslySetInnerHTML.
 */
export interface AttachmentContentMatch {
  attachment_id: string;
  search_rank: number;
  snippet: string | null;
  page_number: number | null;
}

/**
 * Calls the Postgres RPC that runs FTS over Docling-extracted text in
 * encompass_attachments for one loan. Returns ranked matches with sentinel-
 * marked snippets and best-effort page numbers.
 *
 * Returns [] without hitting the network for queries < 2 chars (matches the
 * server-side short-circuit, so we never bother the DB with noise).
 *
 * Pass an AbortSignal so superseded queries don't decorate state.
 */
export async function searchLoanAttachmentContent(
  loanId: string,
  query: string,
  signal?: AbortSignal
): Promise<AttachmentContentMatch[]> {
  const trimmed = (query ?? '').trim();
  if (trimmed.length < 2) {
    return [];
  }

  let builder = supabase.rpc('search_loan_attachment_content', {
    p_loan_id: loanId,
    p_query: trimmed,
    p_limit: 100
  });

  if (signal) {
    builder = builder.abortSignal(signal);
  }

  const { data, error } = await builder;

  if (error) {
    if (signal?.aborted) {
      return [];
    }
    console.error('Content search failed:', error);
    throw error;
  }

  return (data ?? []) as AttachmentContentMatch[];
}

/**
 * Merges content-match metadata onto an attachment for UI rendering. Pure
 * helper exported for the hook + tests.
 */
export function decorateAttachmentMatch(
  attachment: EncompassAttachment,
  query: string,
  contentMatch?: AttachmentContentMatch
): EncompassAttachment {
  const trimmed = (query ?? '').trim().toLowerCase();
  if (!trimmed && !contentMatch) {
    return attachment;
  }

  const titleHit = trimmed.length > 0 && (attachment.title ?? '').toLowerCase().includes(trimmed);
  const folderHit = trimmed.length > 0 && (attachment.assigned_folder ?? '').toLowerCase().includes(trimmed);

  if (!titleHit && !folderHit && !contentMatch) {
    return attachment;
  }

  const matchInfo: EncompassAttachmentMatchInfo = {
    matchedInTitle: titleHit || undefined,
    matchedInFolder: folderHit || undefined,
    matchedInContent: contentMatch ? true : undefined,
    snippet: contentMatch?.snippet ?? undefined,
    pageNumber: contentMatch?.page_number ?? undefined,
    rank: contentMatch?.search_rank ?? undefined
  };

  return { ...attachment, matchInfo };
}

/**
 * Triggers a manual document sync for a loan
 * Calls the encompass-document-sync edge function
 */
export async function triggerDocumentSync(loanId: string): Promise<{
  success: boolean;
  message: string;
  documentsTotal?: number;
  attachmentsTotal?: number;
}> {
  try {
    const { data, error } = await supabase.functions.invoke('encompass-document-sync', {
      body: { loanId, downloadFiles: true, downloadLimit: 15 }  // Explicit limit to prevent 504 timeouts
    });

    if (error) {
      throw error;
    }

    return {
      success: data.success,
      message: data.success
        ? `Synced ${data.summary?.attachmentsTotal || 0} documents`
        : 'Sync failed',
      documentsTotal: data.summary?.documentsTotal,
      attachmentsTotal: data.summary?.attachmentsTotal
    };
  } catch (error) {
    console.error('Document sync failed:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Sync failed'
    };
  }
}

/**
 * Formats file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (!bytes) {
return '—';
}
  if (bytes < 1024) {
return `${bytes} B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)} KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Formats relative time for display
 */
export function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) {
return 'Never';
}

  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
return 'Just now';
}
  if (diffMins < 60) {
return `${diffMins} min ago`;
}
  if (diffHours < 24) {
return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
}
  if (diffDays < 7) {
return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

  return date.toLocaleDateString();
}

// ============================================================================
// Document Group Functions
// ============================================================================

/**
 * Groups documents by their document_groups field from Encompass
 * Documents without groups go to "Other Documents"
 */
export function groupDocumentsByEncompassGroups(
  documents: EncompassDocumentWithAttachments[]
): EncompassDocumentGroup[] {
  // Group documents by their primary group (first in document_groups array)
  const groupMap = new Map<string, {
    folders: EncompassDocumentWithAttachments[];
  }>();

  for (const doc of documents) {
    // Use the first document group from Encompass, or "Other Documents" if none
    const groupName = (doc.document_groups && doc.document_groups.length > 0)
      ? doc.document_groups[0]
      : 'Other Documents';

    if (!groupMap.has(groupName)) {
      groupMap.set(groupName, { folders: [] });
    }
    groupMap.get(groupName)!.folders.push(doc);
  }

  // Convert to array and calculate stats
  const groups: EncompassDocumentGroup[] = [];

  for (const [groupName, { folders }] of groupMap.entries()) {
    // Calculate totals for all attachments in this group
    let totalAttachments = 0;
    let downloadedCount = 0;
    let pendingCount = 0;
    let failedCount = 0;

    for (const folder of folders) {
      for (const att of folder.attachments) {
        totalAttachments++;
        if (att.download_status === 'downloaded') {
          downloadedCount++;
        } else if (att.download_status === 'pending') {
          pendingCount++;
        } else if (att.download_status === 'failed') {
          failedCount++;
        }
      }
    }

    // Only include groups that have attachments
    if (totalAttachments > 0) {
      groups.push({
        groupName,
        displayName: formatGroupName(groupName),
        folders,
        totalAttachments,
        downloadedCount,
        pendingCount,
        failedCount,
        order: groupName === 'Other Documents' ? 999 : 0
      });
    }
  }

  // Sort alphabetically, with "Other Documents" last
  groups.sort((a, b) => {
    if (a.groupName === 'Other Documents') {
return 1;
}
    if (b.groupName === 'Other Documents') {
return -1;
}
    return a.displayName.localeCompare(b.displayName);
  });

  return groups;
}

/**
 * Formats group name for display
 */
function formatGroupName(groupName: string): string {
  if (!groupName) {
return 'Other Documents';
}
  // Already formatted from Encompass
  return groupName;
}

// ============================================================================
// Download Helper Functions
// ============================================================================

/**
 * Triggers download of a file from a signed URL
 * Fetches as blob to force download (cross-origin URLs ignore download attribute)
 */
export async function triggerDownload(url: string, fileName: string): Promise<void> {
  // Fetch the file as a blob to bypass cross-origin download restrictions
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up blob URL after a short delay
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
}

// ============================================================================
// Filename Resolution
// ============================================================================

/** Known file extensions for attachment downloads */
const KNOWN_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'tif', 'tiff', 'webp',
  'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'rtf', 'xml', 'htm', 'html'
]);

/**
 * Resolves a safe download filename for an attachment.
 * Priority: file_name > title > 'document'
 * Sanitizes reserved characters and appends extension from storage_path if missing.
 *
 * NOTE: file_type values from Encompass are category labels (Cloud, Image, Native, Background),
 * NOT MIME types — so extension is derived exclusively from storage_path.
 */
export function resolveAttachmentFilename(attachment: {
  file_name?: string | null;
  title?: string | null;
  storage_path?: string | null;
}): string {
  const raw = attachment.file_name || attachment.title || 'document';

  // Sanitize: remove path separators, OS-reserved characters, and trailing dots/spaces (Windows compat)
  const sanitized = raw.replace(/[/\\:*?"<>|]/g, '_').replace(/[. ]+$/, '').trim() || 'document';

  // Check if it already ends with a known extension
  const dotIdx = sanitized.lastIndexOf('.');
  if (dotIdx > 0) {
    const ext = sanitized.slice(dotIdx + 1).toLowerCase();
    if (KNOWN_EXTENSIONS.has(ext)) {
return sanitized;
}
  }

  // Derive extension from storage_path (only reliable source — file_type is not MIME)
  if (attachment.storage_path) {
    const pathExt = attachment.storage_path.split('.').pop()?.toLowerCase();
    if (pathExt && KNOWN_EXTENSIONS.has(pathExt)) {
return `${sanitized}.${pathExt}`;
}
  }

  // Default fallback
  return `${sanitized}.pdf`;
}
